/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow

import type { Element } from 'react';

import React from 'react';
import { Col, Form, FormGroup, Label } from 'reactstrap';
import {
  CITY,
  COUNTRY,
  MODEL_NAME,
  STATE,
  STREET,
  ZIP,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import { FieldFeedback, RenderInput } from '#components/support/forms/utility/index.js';
import Field from '#components/pages/Models/Model/sections/support/forms/Field/index.js';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import { MODEL_TYPE_1_ID } from '#src/js/constants/models/modelTypes/index.js';
import StatePicker from './StatePicker/index.jsx';
import ProjectSelector from './ProjectSelector/index.js';
import CountrySelectMenu from './CountrySelectMenu/index.jsx';

const buildStateLabel = country => {
  if (country === 'US') {
    return 'State';
  }
  if (country === 'CA') {
    return 'Province / Territory';
  }
  return 'State / Province / Region';
};

const buildZipLabel = country => {
  if (country === 'US') {
    return 'Zip Code';
  }
  if (country === 'CA') {
    return 'Postal Code';
  }
  return 'Zip Code';
};

type Props = {|
  form: string,
  heading: string,
  onNext: Function,
  modelType: number,
  country: string,
|};

const ModelAndSiteInfoSection = ({
  form,
  heading,
  onNext,
  modelType,
  country,
}: Props): Element<'div'> => {
  const onlyUS = modelType === MODEL_TYPE_1_ID;
  const stateLabel = buildStateLabel(country);
  const zipLabel = buildZipLabel(country);

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate>
        <FormGroup tag="fieldset">
          <legend>General Information</legend>
          <ProjectSelector form={form} />
          <FormGroup row>
            <Col lg="9">
              <Label htmlFor={MODEL_NAME}>Model</Label>
              <Field
                form={form}
                id={MODEL_NAME}
                name={MODEL_NAME}
                type="text"
                component={RenderInput}
              />
              <FieldFeedback name={MODEL_NAME} form={form} />
            </Col>
          </FormGroup>
        </FormGroup>
        <FormGroup tag="fieldset">
          <legend>
            Site Address <Tip tipId="siteAddress" />
          </legend>
          <FormGroup row>
            <Col lg="4">
              <Label htmlFor={COUNTRY}>Country</Label>
              <CountrySelectMenu form={form} onlyUS={onlyUS} />
              <FieldFeedback name={COUNTRY} form={form} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col lg="4">
              <Label htmlFor={STREET}>Street</Label>
              <Field form={form} id={STREET} name={STREET} type="text" component={RenderInput} />
              <FieldFeedback name={STREET} form={form} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col lg="4">
              <Label htmlFor={CITY}>City</Label>
              <Field form={form} id={CITY} name={CITY} type="text" component={RenderInput} />
              <FieldFeedback name={CITY} form={form} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col lg="4">
              <Label htmlFor={STATE}>{stateLabel}</Label>
              <StatePicker country={country} form={form} />
              <FieldFeedback name={STATE} form={form} />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col lg="4">
              <Label htmlFor={ZIP}>{zipLabel}</Label>
              <Field form={form} id={ZIP} name={ZIP} type="text" component={RenderInput} />
              <FieldFeedback name={ZIP} form={form} />
            </Col>
          </FormGroup>
        </FormGroup>
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default ModelAndSiteInfoSection;
